import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Panel, Field } from "./ui";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Changing the address the whole account hangs from.
 *
 * The form asks for the password and the authenticator code again even though the
 * person is already signed in. That is not friction for its own sake — a stolen
 * session is the ordinary attack, and an attacker who can silently move the address
 * owns the account permanently, because password reset then goes to them.
 *
 * Nothing changes until the link sent to the *new* address is opened, and this
 * component redeems that link when it arrives back as ?email_token=…
 */
export default function EmailChange() {
  const { t } = useTranslation();
  const { user, patchUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState({ new_email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(user?.pending_email || null);

  useEffect(() => { setPending(user?.pending_email || null); }, [user?.pending_email]);

  // Redeeming the link is the only step that must work while signed out, so it runs
  // on mount rather than behind a button.
  useEffect(() => {
    const token = params.get("email_token");
    if (!token) return;
    api.confirmEmailChange(token)
      .then(({ data }) => {
        toast.success(t("emailChange.confirmed", { email: data.email }));
        setPending(null);
        patchUser?.({ email: data.email, pending_email: null });
      })
      .catch((e) => toast.error(e.response?.data?.detail || t("dialogs.failed")))
      .finally(() => { params.delete("email_token"); setParams(params, { replace: true }); });
    // Intentionally mount-only: redeeming the link twice would just fail the second
    // time, and the token is removed from the URL as soon as it is used.
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.requestEmailChange(form);
      setPending(data.pending_email);
      setForm({ new_email: "", password: "", code: "" });
      toast.success(t("emailChange.sent", { email: data.pending_email }));
    } catch (err) { toast.error(err.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    try { await api.cancelEmailChange(); setPending(null); toast.success(t("emailChange.cancelled")); }
    catch { toast.error(t("dialogs.failed")); }
  };

  return (
    <Panel className="card p-5 sm:p-6" testid="email-change">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={17} className="text-brand-500" />
        <h3 className="font-heading font-semibold tracking-tight text-lg">{t("emailChange.title")}</h3>
      </div>
      <p className="text-sm text-slate-500">{t("emailChange.body", { email: user?.email || "" })}</p>

      {pending ? (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start justify-between gap-3"
             data-testid="email-change-pending">
          <span>{t("emailChange.pending", { email: pending })}</span>
          <button className="btn-ghost text-xs shrink-0" onClick={cancel} data-testid="email-change-cancel">
            <X size={13} /> {t("common.cancel")}
          </button>
        </div>
      ) : (
        <form className="mt-4 space-y-3" onSubmit={submit}>
          <Field label={t("emailChange.newEmail")}>
            <input className="input" type="email" required autoComplete="email"
                   data-testid="email-change-new" value={form.new_email}
                   onChange={(e) => setForm({ ...form, new_email: e.target.value })} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t("emailChange.password")}>
              <input className="input" type="password" required autoComplete="current-password"
                     data-testid="email-change-password" value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
            <Field label={t("emailChange.code")}>
              <input className="input tabular" inputMode="numeric" required maxLength={6}
                     data-testid="email-change-code" value={form.code}
                     onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, "") })} />
            </Field>
          </div>
          <p className="text-[11px] text-slate-400">{t("emailChange.hint")}</p>
          <button className="btn-primary" type="submit" disabled={busy} data-testid="email-change-submit">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            {t("emailChange.submit")}
          </button>
        </form>
      )}
    </Panel>
  );
}
